import { AlertErrorCircle } from '@/assets/icons'
import { UseFormRegisterReturn } from 'react-hook-form'
import { Lock } from '@/assets/icons'
import StringTooltip from '@/components/ui/StringTooltip'

interface FloatLabelFormInputProps {
  label: string
  id: string
  required?: boolean
  error?: string
  disabled?: boolean
  inlineMessage?: string
  registration: UseFormRegisterReturn
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
  tooltipProps?: {
    title: string
    copyToClipboard?: boolean
  }
}

/**
 * Float Label Form Input
 *
 * An input field with floating label support.
 *
 * UX Behavior:
 * - Label appears centered like a placeholder by default
 * - Label floats above the input when focused or when a value is present
 */
export default function FloatLabelFormInput(props: FloatLabelFormInputProps) {
  const { label, id, required, error, disabled, inlineMessage, registration, inputProps, tooltipProps } = props
  return (
    <div className="flex flex-col gap-1">
      <div className="float-label relative">
        <StringTooltip title={tooltipProps?.title || ''} placement="bottom-start" copyToClipboard={tooltipProps?.copyToClipboard}>
          <input
            className={`w-full rounded border-0 ring-1 ring-inset focus:ring-inset focus:outline-none disabled:bg-brand-neutral-100 disabled:text-brand-neutral-700 disabled:cursor-not-allowed ${
              error ? 'ring-red-400 focus:ring-red-400' : 'ring-brand-neutral-400 focus:ring-brand-400'
            }`}
            id={id}
            placeholder=" " // required to activate :placeholder-shown - without it, the label won't float up when the input has a value
            autoComplete="off"
            aria-invalid={!!error}
            aria-required={required}
            disabled={disabled}
            {...registration}
            {...inputProps}
          />
        </StringTooltip>
        <label htmlFor={id} className="text-brand-neutral-600">
          {label}
          {required && <span className="text-red-700 ml-[2px]">*</span>}
        </label>
        {/* Disabled email mask and lock icon */}
        {!!disabled && (
          <>
            <div
              className="absolute inset-0 rounded pointer-events-none ring-1 ring-inset ring-brand-neutral-300"
              style={{
                background: 'linear-gradient(to right, transparent 0%, transparent 70%, #F6F3ED 90%)',
              }}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <Lock className="h-6 w-6 text-brand-neutral-600 cursor-not-allowed" />
            </div>
          </>
        )}
      </div>
      {error && (
        <p className="p-2 mt-1 flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
          <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" />
          {error}
        </p>
      )}
      {!!inlineMessage && <p className="text-xs text-brand-neutral-600 bg-brand-neutral-100 rounded py-2 px-3 mt-1">{inlineMessage}</p>}
    </div>
  )
}
